/**
 * Attachment Uploader Component
 * =============================
 * 
 * Core file upload component that provides file selection, validation, and processing
 * for form attachments. This component handles the actual file upload workflow and
 * integrates with the AttachmentUploadService for data management.
 * 
 * Purpose:
 * - Provides file selection interface (drag & drop, file picker)
 * - Validates file types, sizes, and requirements
 * - Processes and stores uploaded files
 * - Manages upload state and user feedback
 * - Integrates with form validation workflows
 * 
 * Core Features:
 * - Multiple file selection and batch uploads
 * - Drag & drop interface for intuitive file handling
 * - File type and size validation with user feedback
 * - Progress indicators for large file uploads
 * - Error handling and user notification
 * - Responsive design for mobile and desktop
 * - Mobile-specific attachment options (camera, photo library, documents)
 * - Action sheet interface for mobile attachment selection
 * 
 * Technical Implementation:
 * - File API integration for modern browser support
 * - Drag & drop event handling for enhanced UX
 * - File validation and processing pipeline
 * - Service integration for attachment persistence
 * - Reactive state management for upload status
 * - Mobile-optimized file input handling
 * 
 * Validation Features:
 * - File type restrictions based on form requirements
 * - File size limits with user-friendly error messages
 * - Required field validation integration
 * - Duplicate file detection and prevention
 * 
 * Mobile Features:
 * - Camera integration for photo/video capture
 * - Photo library browsing with multiple selection
 * - Document selection with file type filtering
 * - Action sheet presentation for mobile UX
 * - Touch-optimized file input handling
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnDestroy, computed, effect } from '@angular/core';
import { ActionSheetController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../../../services/toast.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { AttachmentUploadInfo } from '../../../../../models/forms/attachments/form-attachment-requirements';
import { ScreenSizeService } from '../../../../../services/screen-size.service';

@Component({
  selector: 'app-attachment-uploader',
  templateUrl: './attachment-uploader.component.html',
  styleUrls: ['./attachment-uploader.component.css']
})
export class AttachmentUploaderComponent implements OnDestroy {
  @Input() attachmentUploadInfo!: AttachmentUploadInfo;

  private dataLoadingSubscription?: Subscription;
  readonly DEFAULT_MAX_ATTACHMENTS = 15;

  constructor(
    private toastService: ToastService,
    private attachmentService: AttachmentUploadService,
    private screenSizeService: ScreenSizeService,
    private actionSheetController: ActionSheetController
  ) {
    // Track screen size changes
    effect(() => {
      const isMobile = this.screenSizeService.isMobileDevice();
    });
  }

  get isMobile() {
    return this.screenSizeService.isMobileDevice();
  }

  // Computed signal to check if max attachments are met
  readonly maxAttachmentsMet = computed(() => {
    const attachments = this.attachmentService.getAttachments(this.attachmentUploadInfo.identifier);
    return attachments.length >= this.DEFAULT_MAX_ATTACHMENTS;
  });

  // Computed signal to check if minimum attachments are met
  readonly minAttachmentsMet = computed(() => {
    const attachments = this.attachmentService.getAttachments(this.attachmentUploadInfo.identifier);
    return attachments.length >= this.attachmentUploadInfo.required;
  });

  // Computed signal to get remaining attachment slots
  readonly remainingAttachmentSlots = computed(() => {
    const attachments = this.attachmentService.getAttachments(this.attachmentUploadInfo.identifier);
    return Math.max(0, this.DEFAULT_MAX_ATTACHMENTS - attachments.length);
  });

  // Computed signal to get current attachment count
  readonly currentAttachmentCount = computed(() => {
    const attachments = this.attachmentService.getAttachments(this.attachmentUploadInfo.identifier);
    return attachments.length;
  });

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this.dataLoadingSubscription?.unsubscribe();
  }
  
  /**
   * DEPRECATED: Mobile-specific method to present attachment options in an action sheet.
   * Provides a native mobile experience for selecting attachment sources including camera,
   * photo library, and documents. This method is deprecated and should be replaced
   * with a more modern implementation.
   * 
   * @returns Promise<void> - Resolves when the action sheet is presented
   */
  async presentAttachmentOptions() {
    if (this.maxAttachmentsMet()) {
      this.toastService.showWarningMessage('Maximum attachments reached');
      return;
    }

    const actionSheet = await this.actionSheetController.create({
      header: 'Add Attachments',
      buttons: [
        {
          text: 'Camera',
          icon: 'camera-outline',
          handler: () => {
            this.takePhotoVideo();
          }
        },
        {
          text: 'Photo Library',
          icon: 'images-outline',
          handler: () => {
            this.selectFromPhotoLibrary();
          }
        },
        {
          text: 'Documents',
          icon: 'document-outline',
          handler: () => {
            this.selectDocuments();
          }
        },
        {
          text: 'Cancel',
          icon: 'close-outline',
          role: 'cancel'
        }
      ]
    });

    await actionSheet.present();
  }

  /**
   * Mobile-specific method to capture photos or videos using the device camera.
   * Creates a file input element with camera capture attribute set to 'environment'
   * (rear-facing camera) for optimal photo/video capture. Handles cleanup of DOM
   * elements and processes captured files through the attachment service.
   * 
   * Note: This method is designed for mobile devices and may not work on desktop.
   */
  takePhotoVideo() {
    if (this.maxAttachmentsMet()) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.capture = 'environment';
    
    const cleanup = () => {
      input.value = '';
      input.remove();
    };
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        this.attachmentService.processFile(file, this.attachmentUploadInfo.identifier).then(() => cleanup());
      } else {
        cleanup();
      }
    };
    
    input.oncancel = cleanup;
    input.click();
  }

  /**
   * Mobile-specific method to browse and select photos/videos from the device's
   * photo library. Creates a file input element configured for image and video
   * file types with multiple selection enabled. Processes selected files through
   * the attachment service after validation.
   * 
   * Note: This method is optimized for mobile photo library access.
   */
  selectFromPhotoLibrary() {
    if (this.maxAttachmentsMet()) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*,';
    input.multiple = true;
    
    this.handleFileSelection(input, 'photo/video library');
  }

  /**
   * Mobile-specific method to browse and select document files from the device.
   * Creates a file input element configured for common document types including
   * PDFs, Word documents, text files, and RTF files. Supports multiple file
   * selection and processes valid files through the attachment service.
   * 
   * Note: This method is designed for mobile document browsing.
   */
  selectDocuments() {
    if (this.maxAttachmentsMet()) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,.pdf,.doc,.docx,.txt,.rtf';
    input.multiple = true;
    
    this.handleFileSelection(input, 'documents');
  }

  /**
   * Desktop-optimized method to browse and select files from the local file system.
   * Creates a file input element configured for all supported file types including
   * images, videos, and documents. Supports multiple file selection and processes
   * valid files through the attachment service. This method is primarily intended
   * for desktop users but works on mobile devices as well.
   */
  selectFromLibrary() {
    if (this.maxAttachmentsMet()) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*,application/pdf,.pdf,.doc,.docx,.txt,.rtf';
    input.multiple = true;
    
    this.handleFileSelection(input, 'library');
  }

  /**
   * Common file selection handler that processes files selected through file input elements.
   * Validates file types, checks remaining attachment slots, and processes valid files
   * through the attachment service. Provides user feedback for file count limitations
   * and invalid file types.
   * 
   * @param input - HTMLInputElement configured for file selection
   * @param source - String identifier for the source of file selection (e.g., 'library', 'photo/video library', 'documents')
   * @private
   */
  private handleFileSelection(input: HTMLInputElement, source: string) {
    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const remainingSlots = this.remainingAttachmentSlots();
        const filesToProcess = Array.from(files).slice(0, remainingSlots);
        
        if (files.length > remainingSlots) {
          this.toastService.showWarningMessage(`Only ${remainingSlots} more attachment${remainingSlots === 1 ? '' : 's'} can be added`);
        }
        
        filesToProcess.forEach(file => {
          if (this.isValidFileType(file)) {
            this.attachmentService.processFile(file, this.attachmentUploadInfo.identifier);
          } else {
            this.toastService.showWarningMessage(`${file.name} is not a supported file type`);
          }
        });
      }
    };
    
    input.click();
  }

  /**
   * Validates file types against a predefined list of supported formats.
   * Checks both MIME types and file extensions to ensure compatibility.
   * Supports images (JPEG, PNG, GIF, WebP, BMP), videos (MP4, AVI, MOV, WMV, FLV, WebM, MKV),
   * and documents (PDF, Word, TXT, RTF).
   * 
   * @param file - File object to validate
   * @returns boolean - True if file type is supported, false otherwise
   * @private
   */
  private isValidFileType(file: File): boolean {
    const validTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
      // Videos
      'video/mp4', 'video/avi', 'video/mov', 'video/quicktime', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv',
      // Documents
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', 'text/rtf'
    ];
    
    return validTypes.includes(file.type) || file.name.toLowerCase().endsWith('.pdf');
  }

  /**
   * Handles drag over events for the file drop zone.
   * Prevents default browser behavior and adds visual feedback by applying
   * the 'drag-over' CSS class. Only active when maximum attachments haven't been reached.
   * 
   * @param event - DragEvent object from the browser
   */
  onDragOver(event: DragEvent) {
    if (this.maxAttachmentsMet()) return;
    
    event.preventDefault();
    event.stopPropagation();
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.add('drag-over');
  }

  /**
   * Handles drag leave events for the file drop zone.
   * Prevents default browser behavior and removes visual feedback by removing
   * the 'drag-over' CSS class. Only active when maximum attachments haven't been reached.
   * 
   * @param event - DragEvent object from the browser
   */
  onDragLeave(event: DragEvent) {
    if (this.maxAttachmentsMet()) return;
    
    event.preventDefault();
    event.stopPropagation();
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.remove('drag-over');
  }

  /**
   * Handles file drop events for the file drop zone.
   * Prevents default browser behavior, removes visual feedback, and processes
   * dropped files. Validates file types, checks remaining attachment slots,
   * and processes valid files through the attachment service. Provides user
   * feedback for file count limitations and invalid file types.
   * 
   * @param event - DragEvent object containing dropped files
   */
  onDrop(event: DragEvent) {
    if (this.maxAttachmentsMet()) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.remove('drag-over');
    
    if (event.dataTransfer?.files.length) {

      const remainingSlots = this.remainingAttachmentSlots();
      const validFiles = Array.from(event.dataTransfer.files)
        .filter(file => this.isValidFileType(file))
        .slice(0, remainingSlots);
      
      if (event.dataTransfer.files.length > remainingSlots) {
        this.toastService.showWarningMessage(`Only ${remainingSlots} more attachment${remainingSlots === 1 ? '' : 's'} can be added`);
      }
      
      validFiles.forEach(file => {
        this.attachmentService.processFile(file, this.attachmentUploadInfo.identifier);
      });

      // Show warnings for invalid file types
      Array.from(event.dataTransfer.files)
        .filter(file => !this.isValidFileType(file))
        .forEach(file => {
          this.toastService.showWarningMessage(`${file.name} is not a supported file type`);
        });
    }
  }
}